/**
 * Geocoding de lugares vía Nominatim (OpenStreetMap). No pasa por `httpClient`:
 * es un host externo, sin JWT, y rechaza headers de API que el cliente nuestro
 * adjuntaría por default.
 */

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const AR_VIEWBOX = '-73.5,-55.1,-53.6,-21.8';
const DEFAULT_LIMIT = 6;
const ADMIN_KIND_MIN_SCORE = 50;

export const PLACE_SEARCH_MIN_CHARS = 2;

/** `location.kind` del parse → tipos Nominatim (`addresstype` / `type`). */
const KIND_NOMINATIM_TYPES = {
  address: ['house', 'building', 'place'],
  street: ['road', 'residential', 'pedestrian', 'living_street', 'primary', 'secondary', 'tertiary', 'unclassified'],
  neighborhood: ['suburb', 'neighbourhood', 'quarter', 'city_block'],
  municipality: ['city', 'municipality', 'town'],
  locality: ['city', 'town', 'village', 'hamlet', 'locality', 'municipality'],
  department: ['county'],
  province: ['state'],
};

const ADMIN_KINDS = new Set(['neighborhood', 'municipality', 'locality', 'department', 'province']);

/**
 * `countrycodes=ar` ya recorta el país. El token “Argentina” hace que Nominatim
 * matchee barrios/POIs (“Villa Argentina”) en vez del municipio.
 * @param {string} query
 * @returns {string}
 */
export function stripNominatimCountrySuffix(query) {
  return String(query || '').replace(/,\s*Argentina\s*$/i, '').trim();
}

function nominatimTypeOf(hit) {
  return hit?.addresstype || hit?.type || '';
}

function bboxArea(hit) {
  const [south, north, west, east] = (hit?.boundingbox || []).map(Number);
  if (![south, north, west, east].every(Number.isFinite)) return 0;
  return Math.abs(north - south) * Math.abs(east - west);
}

function normalizePlaceText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Partes de `location.query` después del nombre (ciudad, provincia).
 * Sirven para no elegir un homónimo (Saldán vs Capital, otra Villa Belgrano).
 * @param {string} [query]
 * @returns {string[]}
 */
export function contextTokensFromPlaceQuery(query) {
  const stripped = stripNominatimCountrySuffix(query);
  return stripped.split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(1)
    .filter((part) => normalizePlaceText(part).length >= 3);
}

function hitHaystack(hit) {
  const addr = hit?.address || {};
  return normalizePlaceText([
    hit?.display_name,
    addr.city,
    addr.town,
    addr.municipality,
    addr.suburb,
    addr.county,
    addr.state,
    addr.state_district,
    addr.village,
  ].filter(Boolean).join(', '));
}

function hitMatchesKind(hit, kind) {
  const types = KIND_NOMINATIM_TYPES[kind] || [];
  return types.includes(nominatimTypeOf(hit)) || types.includes(hit?.type);
}

/**
 * “Córdoba Capital” no aparece así en Nominatim (“Córdoba, … Departamento Capital”).
 * Se matchea la ciudad y, si pidieron Capital, la palabra capital.
 */
function scoreContextMatch(hit, tokens) {
  if (!tokens.length) return 0;
  const hay = hitHaystack(hit);
  let score = 0;
  let wantsCapital = false;

  tokens.forEach((token) => {
    const needle = normalizePlaceText(token);
    if (!needle) return;
    if (/\bcapital\b/.test(needle)) wantsCapital = true;
    if (hay.includes(needle)) {
      score += 80;
      return;
    }
    const withoutCapital = needle.replace(/\bcapital\b/g, ' ').replace(/\s+/g, ' ').trim();
    if (withoutCapital && hay.includes(withoutCapital)) score += 50;
  });

  if (wantsCapital) score += /\bcapital\b/.test(hay) ? 70 : -50;
  else if (score === 0) score -= 40;
  return score;
}

/**
 * Puntaje para elegir el hit que corresponde a `location.kind`, no el 1.er
 * ranking de Nominatim (calles “Córdoba” en un departamento Capital, un POI).
 * @param {object} hit
 * @param {string} [kind]
 * @param {string[]} [contextTokens]
 * @returns {number}
 */
export function scorePlaceHitForKind(hit, kind, contextTokens = []) {
  if (!hit) return -Infinity;
  const types = KIND_NOMINATIM_TYPES[kind] || [];
  const type = nominatimTypeOf(hit);
  let score = 0;
  if (types.includes(type) || types.includes(hit.type)) score += 100;
  if (hit.osm_type === 'relation') score += 50;
  if (hit.class === 'boundary' || hit.class === 'place') score += 30;
  if (ADMIN_KINDS.has(kind)) {
    if (hit.class === 'highway' || hit.class === 'amenity' || hit.class === 'shop' || hit.class === 'leisure') {
      score -= 80;
    }
    score += Math.min(40, bboxArea(hit) * 500);
  }
  if (kind === 'neighborhood' && ['city', 'town', 'village', 'municipality', 'state'].includes(type)) {
    score -= 120;
  }
  if (kind === 'street' && hit.class === 'highway') score += 80;
  if (kind === 'address' && (hit.class === 'place' || hit.class === 'building')) score += 40;
  score += scoreContextMatch(hit, contextTokens);
  return score;
}

/**
 * @param {object[]} results
 * @param {string} [kind]
 * @param {{ contextQuery?: string }} [options]
 * @returns {object|null}
 */
export function pickPlaceHitForKind(results, kind, { contextQuery } = {}) {
  if (!Array.isArray(results) || !results.length) return null;
  if (!kind) return results[0];
  const contextTokens = contextTokensFromPlaceQuery(contextQuery);

  let best = results[0];
  let bestScore = scorePlaceHitForKind(best, kind, contextTokens);
  results.forEach((hit) => {
    const score = scorePlaceHitForKind(hit, kind, contextTokens);
    if (score > bestScore) {
      best = hit;
      bestScore = score;
    }
  });

  if (bestScore < ADMIN_KIND_MIN_SCORE && !hitMatchesKind(best, kind)) return null;
  return best;
}

/**
 * @param {string} query
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {number} [options.limit]
 * @returns {Promise<object[]>} Hits crudos de Nominatim.
 */
export const searchPlaces = async (query, { signal, limit = DEFAULT_LIMIT } = {}) => {
  const q = String(query || '').trim();
  if (q.length < PLACE_SEARCH_MIN_CHARS) return [];

  const params = new URLSearchParams({
    q,
    format: 'json',
    countrycodes: 'ar',
    limit: String(limit),
    addressdetails: '1',
    viewbox: AR_VIEWBOX,
    bounded: '0',
    'accept-language': 'es',
  });

  const res = await fetch(`${NOMINATIM_URL}?${params}`, {
    signal,
    headers: { 'User-Agent': 'MapApp/1.0' },
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return Array.isArray(json) ? json : [];
};

/**
 * @param {object} result - Hit de Nominatim.
 * @returns {{ lng: number, lat: number, displayName: string, bbox: number[][]|null }}
 */
export const parsePlaceResult = (result) => {
  const [south, north, west, east] = (result.boundingbox || []).map(Number);
  return {
    lng: parseFloat(result.lon),
    lat: parseFloat(result.lat),
    displayName: result.display_name,
    bbox: result.boundingbox ? [[west, south], [east, north]] : null,
  };
};

/**
 * Geocodifica `location.query` del parse. Prefiere un hit del `kind` (municipio,
 * provincia, barrio) con bbox de relación, no el primer ranking de Nominatim.
 * @param {string} query
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {string} [options.kind]
 * @param {string} [options.contextQuery] `location.query` para desambiguar homónimos.
 * @returns {Promise<object|null>} Target de `onFlyTo`, o `null`.
 */
export const geocodePlaceQuery = async (query, { signal, kind, contextQuery } = {}) => {
  const q = stripNominatimCountrySuffix(query) || String(query || '').trim();
  if (!q) return null;
  const results = await searchPlaces(q, { signal, limit: kind ? 10 : 1 });
  if (!results.length) return null;
  const hit = kind
    ? pickPlaceHitForKind(results, kind, { contextQuery: contextQuery || query })
    : results[0];
  if (!hit) return null;
  return parsePlaceResult(hit);
};
